import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CarMonthlyChargesTab.module.scss';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, DATE_FORMAT_MONTH_YEAR } from 'Config';
import { useHistory, useParams } from 'react-router-dom';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import VehiclesService from 'api/vehicles/VehiclesService';
import Loading from 'common/services/Loading';
import DateFormat from 'common/components/dateFormat/dateFormat';
import CarMonthlyChargesFiltersScreen, { Filters } from './components/CarMonthlyChargesFiltersScreen';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import { VehicleMonthlyCostsDto } from 'api/vehicleMonthlyCosts/models/VehicleMonthlyCostsDto';
import { VehicleMonthlyCostsSearchCriteria } from 'api/vehicleMonthlyCosts/models/VehicleMonthlyCostsSearchCriteria';
import Popover from 'common/components/popover/Popover';
import dayjs from 'dayjs';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import MaintenancesIcon from 'assets/svg/maintenance.svg';
import CostsIcon from 'assets/svg/coins.svg';
import CoinsIcon from 'assets/svg/coins.svg';

import InfoCard from 'common/components/infoCard/InfoCard';
import ReactTooltip from 'react-tooltip';
import colors from 'styles/export/colors.module.scss';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

const INFO_CARD_COLORS = [
    colors.counterTeal1,
    colors.counterTeal2,
    colors.counterBlue1,
    colors.counterBlue2,
];

const FILTERS_CACHE_KEY = 'vehicle-monthly-charges-filters';

type Props = {
    tourReRender: () => void;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const CarMonthlyChargesTab = ({ tourReRender, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();

    const { vehicleId } = useParams<{ vehicleId: string }>();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);

    const [charges, setCharges] = useState<VehicleMonthlyCostsDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [counters, setCounters] = useState<VehicleMonthlyCostsDto | null>(null);

    const [criteria, setCriteria] = useState<VehicleMonthlyCostsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        vehicleId: vehicleId,
        endDate: dayjs().endOf('month').toDate(),
        orderColumn: 'vmc.date',
        orderBy: 'desc'
    });

    const [filters, setFilters] = useState<Filters>({ endDate: criteria.endDate });
    const [filtersTotal, setFiltersTotal] = useState(1);
    const isFirstLoad = useRef<boolean>(true);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    // Bidirectional scroll hook
    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
        finishRestoration,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
    } = bidirectionalScroll;

    const getData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const [chargesResult, countersResult] = await Promise.all([
                VehiclesService.getMonthlyCosts(criteria),
                !isLoadMore ? VehiclesService.getCostsCounters({
                    ...criteria,
                    orderBy: undefined,
                    orderColumn: undefined,
                }) : Promise.resolve(null),
            ]);

            setTotalItems(chargesResult.totalItems);

            if (isLoadMore) {
                setCharges(prev => [...prev, ...chargesResult.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setCharges(chargesResult.items);
                if (countersResult) {
                    setCounters(countersResult);
                }
            }

            ReactTooltip.rebuild();
            Loading.hide();
            setIsLoadingMore(false);

            // When restoring scroll, load adjacent pages
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(chargesResult.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await VehiclesService.getMonthlyCosts(prevCriteria);
                        setCharges(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await VehiclesService.getMonthlyCosts(nextCriteria);
                        setCharges(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get monthly charges list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        }
        finally {
            tourReRender();
        }
    };

    const navigateToUrl = (type: string) => {
        let url = `/vehicles/details/${vehicleId}/`;
        switch (type.toUpperCase()) {
            case 'CHARGE': url += `costs`; break;
            case 'CONTRACT': url += `contract`; break;
            case 'MAINTENANCE': url += `maintenance`; break;
            default: return '';
        }
        return url + '/list/null/null/null/';
    }

    const onRowClick = (event: any, url: string) => {
        if (event.ctrlKey) {
            window.open(url, '_blank');
            return;
        }
        history.push(url);
    }

    const renderCostCell = (type: string, value: number, date: Date) => {
        return <div className={styles.moneyColumn} onClick={event => onRowClick(event, navigateToUrl(type) + dayjs(date).format('YYYY-MM-01'))}>
            <MoneyFormat fixedDecimalScale={true} value={value} />
        </div>
    }

    const handleOrderChange = (orderData: OrderData<VehicleMonthlyCostsDto>) => {
        setCharges([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || String(orderData.field),
        }));
    }

    const fetchAllForExport = useCallback(async () => {
        const result = await VehiclesService.getMonthlyCosts({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    const dataTableColumns: DataTableColumn<VehicleMonthlyCostsDto>[] = useMemo(() => {
        const euroHeaderNote = `${t('common.unit')}: ${t('common.euro')}`;
        return [
            {
                field: 'date' as keyof VehicleMonthlyCostsDto,
                name: t('vehicle_monthly_costs.month') as string,
                orderColumn: 'vmc.date',
                width: 150,
                exportValue: row => row.date ? dayjs(row.date).format(DATE_FORMAT_MONTH_YEAR.toString()) : '',
                renderCell: row => <DateFormat value={row.date} format={DATE_FORMAT_MONTH_YEAR.toString()} />,
            },
            {
                field: 'contractsCosts' as keyof VehicleMonthlyCostsDto,
                name: t('contracts.title') as string,
                orderColumn: 'contracts_costs',
                width: 160,
                hideOn: ['sm', 'md'],
                columnCellAlignment: 'right',
                headerNote: euroHeaderNote,
                renderCell: row => renderCostCell('contract', row.contractsCosts || 0, row.date),
            },
            {
                field: 'chargesCosts' as keyof VehicleMonthlyCostsDto,
                name: t('charges.title') as string,
                orderColumn: 'charges_costs',
                width: 160,
                hideOn: ['sm', 'md'],
                columnCellAlignment: 'right',
                headerNote: euroHeaderNote,
                renderCell: row => renderCostCell('charge', row.chargesCosts || 0, row.date),
            },
            {
                field: 'maintenancesCosts' as keyof VehicleMonthlyCostsDto,
                name: t('maintenances.title') as string,
                orderColumn: 'maintenances_costs',
                width: 160,
                hideOn: ['sm', 'md'],
                columnCellAlignment: 'right',
                headerNote: euroHeaderNote,
                renderCell: row => renderCostCell('maintenance', row.maintenancesCosts || 0, row.date),
            },
            {
                field: 'id' as keyof VehicleMonthlyCostsDto,
                name: t('vehicle_monthly_costs.total') as string,
                orderColumn: 'total',
                width: 160,
                columnCellAlignment: 'right',
                headerNote: euroHeaderNote,
                exportValue: row => (row?.chargesCosts || 0) + (row?.contractsCosts || 0) + (row?.maintenancesCosts || 0),
                renderCell: row => <div className={styles.moneyColumn}>
                    <MoneyFormat fixedDecimalScale={true} value={(row?.chargesCosts || 0) + (row?.contractsCosts || 0) + (row?.maintenancesCosts || 0)} />
                </div>,
            },
        ];
    }, [t]);

    // Handler for infinite scroll load more
    const loadMore = useCallback(() => {
        if (isLoadingMore || charges.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, charges.length, totalItems, loadedPageRange.max]);

    // Load previous pages (bidirectional scroll)
    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();

            const prevCriteria: VehicleMonthlyCostsSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await VehiclesService.getMonthlyCosts(prevCriteria);

            setCharges(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.CHARGES, `Couldn't get previous monthly charges page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    // Adjust scroll position after prepending items
    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [charges, isLoadingPrevious, adjustScrollAfterPrepend]);

    useEffect(() => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null && isFirstLoad.current) {
            isFirstLoad.current = false;
            onChangeFilters(JSON.parse(cachedFilters));
        }
        else {
            const isLoadMore = criteria.page > loadedPageRange.max && charges.length > 0;
            getData(isLoadMore);
        }
    }, [criteria]);

    useEffect(() => {
        const handleResetFilters = () => {
            const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
            if (cachedFilters != null) {
                onChangeFilters(JSON.parse(cachedFilters));
            }
        };
        window.addEventListener('reset-monthly-charges-filters', handleResetFilters);
        return () => window.removeEventListener('reset-monthly-charges-filters', handleResetFilters);
    }, []);

    const onChangeFilters = (_filters: Filters) => {
        updateTotalFilters(_filters);
        setFilters(_filters);
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(_filters));

        // Reset items when filters change
        setCharges([]);
        resetScrollState();
        setTotalItems(0);

        setCriteria(prev => ({
            ...prev,
            startDate: _filters.startDate,
            endDate: _filters.endDate,
            page: 1,
        }));
    }

    const updateTotalFilters = (_filters: Filters) => {
        let total = 0;
        if (_filters.startDate || _filters.endDate) {
            total++;
        }
        setFiltersTotal(total);
    }

    const renderCounterBoxes = () => {
        return (
            <div className={styles.rowCounters}>
                {counters && (
                    <InfoCard
                        label={t('dashboard.monthly_cost_types.FIXED_COST') as string}
                        value={counters.fixedCosts ?? 0}
                        icon={CostsIcon}
                        backgroundColor={INFO_CARD_COLORS[0]}
                        isMoney
                        className={styles.col2}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('dashboard.monthly_cost_types.VARIABLE_COST') as string}
                        value={counters.variableExpenses ?? 0}
                        icon={MaintenancesIcon}
                        backgroundColor={INFO_CARD_COLORS[1]}
                        isMoney
                        className={styles.col2}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('charges.total_expenses') as string}
                        value={(counters.fixedCosts ?? 0) + (counters.variableExpenses ?? 0)}
                        icon={CoinsIcon}
                        backgroundColor={INFO_CARD_COLORS[2]}
                        isMoney
                        className={styles.col2}
                    />
                )}
                {counters && (
                    <InfoCard
                        label={t('vehicle.average_monthly_cost') as string}
                        value={counters.averageMonthlyCost ?? 0}
                        icon={CoinsIcon}
                        backgroundColor={INFO_CARD_COLORS[3]}
                        isMoney
                        className={styles.col2}
                    />
                )}
            </div>
        );
    }

    return (
        <div className={styles.listWrapper}>
            <div className={styles.tabHeader}>
                <div></div>
                <div className={styles.rowFlex}>
                    <Popover
                        contentContainerClassName={styles.filtersPopoverContainer}
                        containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                        positions={['left']}
                        onClickOutside={() => updateTotalFilters(filters)}
                        content={setIsPopoverOpen => <CarMonthlyChargesFiltersScreen
                            filters={filters}
                            onFilter={(f: Filters) => { setIsPopoverOpen(false); onChangeFilters(f) }}
                            onChange={updateTotalFilters}
                        />}>
                        {(isPopoverOpen, setIsPopoverOpen) => (
                            <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                {filtersTotal > 0 &&
                                    <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                }
                            </ScreenHeaderButton>
                        )}
                    </Popover>
                    <ExportListingButton exportRef={exportListingRef} />
                </div>
            </div>
            {vehicleId && counters && (
                <div className={styles.countersSection}>
                    {renderCounterBoxes()}
                </div>
            )}
            <div className={styles.listTblWrapper}>
                <DataTableWithSidebar
                    cacheKey="MONTHLY_CHARGES_VEHICLE_TABLE"
                    columns={dataTableColumns}
                    rows={charges}
                    defaultOrder={{ field: 'date' as keyof VehicleMonthlyCostsDto, orderColumn: 'vmc.date', isOrderAsc: false }}
                    onOrderChange={handleOrderChange}
                    onRowId={row => row.id ?? ''}
                    scrollToRowId={scrollToItemId}
                    infiniteScroll={{
                        isLoading: isLoadingMore,
                        hasMore: getHasMore(charges.length, totalItems),
                        onLoadMore: getOnLoadMore(loadMore),
                        hasMorePrevious: getHasMorePrevious(),
                        isLoadingPrevious,
                        onLoadPrevious: getOnLoadPrevious(loadPrevious),
                    }}
                    exportExcel={{
                        fetchAll: fetchAllForExport,
                        fileName: t('vehicles.list.tour.monthly_charges' as any) as string,
                    }}
                    exportRef={exportListingRef}
                    hideExportInSidebar={true}
                />
            </div>
        </div>
    );
}

export default CarMonthlyChargesTab;
